import { useState, useCallback } from "react";
import {
  ArrowLeft,
  Plus,
  List,
  Image,
  Settings2,
  Loader2,
  Edit2,
  Check,
  X,
  MoreVertical,
  PackagePlus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { DatePicker } from "@/components/ui/date-picker";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useCameras,
  useCameraPlanColumns,
  useCreateCamera,
  useUpdateCamera,
  useUpdateCameraPlan,
} from "@/hooks/useCameraPlans";
import {
  useProjectEquipmentGroups,
  useCreateEquipmentGroup,
} from "@/hooks/useProjectEquipment";
import { CameraListView } from "./CameraListView";
import { CameraMapView } from "./CameraMapView";
import { ManageColumnsDialog } from "./ManageColumnsDialog";
import { toast } from "sonner";
import { generateUUID } from "@/lib/utils";
import {
  getDraftFieldKeyDownHandlers,
  useDraftField,
} from "@/hooks/useDraftField";
import type { CameraPlan, ZeroDateValue } from "@/types/camera-plans";

type ViewMode = "list" | "map";

function zeroDateToInputValue(value: ZeroDateValue): string {
  if (!value) return "";
  if (typeof value === "number") {
    return new Date(value).toISOString().slice(0, 10);
  }
  return value;
}

interface CameraPlanDetailViewProps {
  plan: CameraPlan;
  projectId: string;
  onBack: () => void;
}

export function CameraPlanDetailView({
  plan,
  projectId,
  onBack,
}: CameraPlanDetailViewProps) {
  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [selectedCameraId, setSelectedCameraId] = useState<string | null>(null);
  const [isColumnsOpen, setIsColumnsOpen] = useState(false);
  const [autoCreateEquipmentGroup, setAutoCreateEquipmentGroup] =
    useState(false);

  const { data: cameras = [], isLoading: camerasLoading } = useCameras(plan.id);
  const { data: columns = [], isLoading: columnsLoading } =
    useCameraPlanColumns(plan.id);
  const { data: equipmentGroups = [] } = useProjectEquipmentGroups(projectId);
  const createCamera = useCreateCamera();
  const updateCamera = useUpdateCamera();
  const updatePlan = useUpdateCameraPlan();
  const createGroup = useCreateEquipmentGroup();

  const isLoading = camerasLoading || columnsLoading;

  const nameDraft = useDraftField<string>({
    value: plan.name,
    toShared: (draft) => draft.trim(),
    onCommit: (name) => {
      if (!name || name === plan.name) return;
      updatePlan.mutate({
        id: plan.id,
        projectId,
        updates: { name },
      });
    },
  });

  const handleAddCamera = () => {
    const maxNum = cameras.reduce((max, c) => {
      const match = c.number.match(/\d+$/);
      return match ? Math.max(max, parseInt(match[0], 10)) : max;
    }, 0);

    const nextNum = maxNum + 1;
    const cameraNumber = String(nextNum);
    const groupName = `CAM${String(nextNum).padStart(2, "0")}`;
    const optimisticId = generateUUID();

    setSelectedCameraId(optimisticId);

    createCamera.mutate(
      {
        optimisticId,
        cameraPlanID: plan.id,
        number: cameraNumber,
        description: null,
        icon: "tripod",
        positionX: null,
        positionY: null,
        rotation: 0,
        track: null,
        customValues: {},
        sortOrder: cameras.length,
        color: getNextColor(cameras.length),
        equipmentGroupID: null,
      },
      {
        onSuccess: (newCamera) => {
          if (autoCreateEquipmentGroup) {
            createGroup.mutate(
              { project_id: projectId, name: groupName },
              {
                onSuccess: (newGroup) => {
                  updateCamera.mutate({
                    id: newCamera.id,
                    cameraPlanId: plan.id,
                    updates: { equipmentGroupID: newGroup.id },
                  });
                },
              },
            );
          }
        },
      },
    );
  };

  const handleCreateMissingGroups = useCallback(async () => {
    const unlinkedCameras = cameras.filter((c) => !c.equipmentGroupID);

    if (unlinkedCameras.length === 0) {
      toast.info("All cameras already have equipment groups");
      return;
    }

    // Group cameras by target group name to avoid duplicates
    const camerasByGroupName = new Map<string, typeof unlinkedCameras>();
    for (const camera of unlinkedCameras) {
      const groupName = cameraNumberToGroupName(camera.number);
      if (!camerasByGroupName.has(groupName)) {
        camerasByGroupName.set(groupName, []);
      }
      camerasByGroupName.get(groupName)!.push(camera);
    }

    // Lookup existing groups by name
    const existingGroupsByName = new Map(
      equipmentGroups.map((g) => [g.name, g.id]),
    );

    let groupsCreated = 0;
    let camerasAssigned = 0;

    try {
      for (const [groupName, groupCameras] of camerasByGroupName) {
        let groupId: string;

        if (existingGroupsByName.has(groupName)) {
          groupId = existingGroupsByName.get(groupName)!;
        } else {
          const newGroup = await createGroup.mutateAsync({
            project_id: projectId,
            name: groupName,
          });
          groupId = newGroup.id;
          groupsCreated++;
          existingGroupsByName.set(groupName, groupId);
        }

        for (const camera of groupCameras) {
          await updateCamera.mutateAsync({
            id: camera.id,
            cameraPlanId: plan.id,
            updates: { equipmentGroupID: groupId },
          });
          camerasAssigned++;
        }
      }

      const parts: string[] = [];
      if (groupsCreated > 0) {
        parts.push(
          `Created ${groupsCreated} group${groupsCreated !== 1 ? "s" : ""}`,
        );
      }
      if (camerasAssigned > 0) {
        parts.push(
          `assigned ${camerasAssigned} camera${camerasAssigned !== 1 ? "s" : ""}`,
        );
      }
      toast.success(parts.join(" and "));
    } catch {
      toast.error("Some operations failed during batch creation");
    }
  }, [cameras, equipmentGroups, createGroup, updateCamera, projectId, plan.id]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header row 1: Back + Title + Add Camera + Menu */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ArrowLeft className="h-4 w-4" />
          </Button>

          {nameDraft.isEditing ? (
            <div className="flex items-center gap-1">
              <Input
                value={nameDraft.draft}
                onChange={(e) => nameDraft.setDraft(e.target.value)}
                onKeyDown={getDraftFieldKeyDownHandlers(nameDraft)}
                className="h-8 text-lg font-semibold w-48"
                autoFocus
              />
              <Button
                offlineEdit
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => void nameDraft.commit()}
              >
                <Check className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={nameDraft.cancel}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <button
              data-offline-edit
              className="text-lg font-semibold hover:underline text-left truncate"
              onClick={nameDraft.start}
            >
              {plan.name}
            </button>
          )}

          {!nameDraft.isEditing && (
            <Button
              offlineEdit
              variant="ghost"
              size="icon"
              className="h-7 w-7 shrink-0"
              onClick={nameDraft.start}
            >
              <Edit2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <Button
            offlineEdit
            size="sm"
            onClick={handleAddCamera}
            disabled={createCamera.isPending}
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Camera
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" className="h-8 w-8">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem offlineEdit onClick={handleCreateMissingGroups}>
                <PackagePlus className="h-4 w-4 mr-2" />
                Create missing equipment groups
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Header row 2: View toggle + Columns + Auto-create */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex rounded-lg border overflow-hidden">
          <Button
            variant={viewMode === "list" ? "secondary" : "ghost"}
            size="sm"
            className="rounded-none"
            onClick={() => setViewMode("list")}
          >
            <List className="h-4 w-4 mr-1" />
            List
          </Button>
          <Button
            variant={viewMode === "map" ? "secondary" : "ghost"}
            size="sm"
            className="rounded-none"
            onClick={() => setViewMode("map")}
          >
            <Image className="h-4 w-4 mr-1" />
            Media
          </Button>
        </div>

        <DatePicker
          value={zeroDateToInputValue(plan.eventDate)}
          onChange={(date) =>
            updatePlan.mutate({
              id: plan.id,
              projectId,
              updates: { eventDate: date || null },
            })
          }
          placeholder="Event date"
          className="w-auto"
        />

        <Select
          value={plan.mapType}
          onValueChange={(v) => {
            const newType = v as "image" | "embed";
            updatePlan.mutate({
              id: plan.id,
              projectId,
              updates: {
                mapType: newType,
                ...(newType === "image" ? { embedURL: null } : {}),
                ...(newType === "embed" ? { imageURL: null } : {}),
              },
            });
          }}
        >
          <SelectTrigger className="w-auto h-8 text-xs gap-1">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="image">Image</SelectItem>
            <SelectItem value="embed">Embed</SelectItem>
          </SelectContent>
        </Select>

        <Button
          offlineEdit
          variant="outline"
          size="sm"
          onClick={() => setIsColumnsOpen(true)}
        >
          <Settings2 className="h-4 w-4 mr-1" />
          Columns
        </Button>

        <div className="flex items-center gap-1.5">
          <Checkbox
            id="auto-create-equipment-group"
            checked={autoCreateEquipmentGroup}
            onCheckedChange={(checked) =>
              setAutoCreateEquipmentGroup(!!checked)
            }
          />
          <Label
            htmlFor="auto-create-equipment-group"
            className="text-xs text-muted-foreground cursor-pointer whitespace-nowrap"
          >
            Auto-create equipment group
          </Label>
        </div>
      </div>

      {/* Content */}
      {viewMode === "list" ? (
        <CameraListView
          cameras={cameras}
          columns={columns}
          cameraPlanId={plan.id}
          projectId={projectId}
          eventDate={plan.eventDate}
          selectedCameraId={selectedCameraId}
          onSelectCamera={setSelectedCameraId}
        />
      ) : (
        <CameraMapView
          plan={plan}
          cameras={cameras}
          cameraPlanId={plan.id}
          projectId={projectId}
          selectedCameraId={selectedCameraId}
          onSelectCamera={setSelectedCameraId}
        />
      )}

      {/* Manage Columns Dialog */}
      <ManageColumnsDialog
        cameraPlanId={plan.id}
        columns={columns}
        open={isColumnsOpen}
        onOpenChange={setIsColumnsOpen}
      />
    </div>
  );
}

/** Derive an equipment group name from a camera number, e.g. "3" → "CAM03". */
function cameraNumberToGroupName(cameraNumber: string): string {
  const num = cameraNumber.match(/\d+$/);
  if (num) {
    return `CAM${num[0].padStart(2, "0")}`;
  }
  return `CAM${cameraNumber}`;
}

const COLORS = [
  "#3b82f6", // blue
  "#ef4444", // red
  "#22c55e", // green
  "#f59e0b", // amber
  "#8b5cf6", // violet
  "#ec4899", // pink
  "#06b6d4", // cyan
  "#f97316", // orange
  "#14b8a6", // teal
  "#6366f1", // indigo
];

function getNextColor(index: number): string {
  return COLORS[index % COLORS.length];
}
